
/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import {distinctUntilChanged, debounceTime, startWith, map} from 'rxjs/operators';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FrameworkUtr, Group, Initiative, Organization } from "../../lib/wwg.classes";
import { FormControl } from "@angular/forms";
import { FrameworkUtrApi } from "../../sdk/services/custom/FrameworkUtrApi";
import { NotificationService } from "../../services/notification.service";

export interface GroupMd extends Group {
  control: FormControl;
  orgControl: FormControl;
  name: string;
  filteredInitiatives: any[];
  filteredOrganizations: any[];
  utrControl: FormControl;
  filteredUtrs: any[];
  selected?: string;
  id?: string;
}

@Component({
  selector: 'wwg-framework-groups',
  templateUrl: './framework-group.component.html',
  styleUrls: ['./framework-group.component.scss']
})
export class FrameworkGroupComponent implements OnInit {

  public groups: GroupMd[] = [];

  public initiatives: Initiative[] = [];
  public isEdit = true;
  public isLoaded = false;

  public customType = 'custom';
  public initiativeType = 'initiative';
  public organizationType = 'organization';
  public frameworkUtr: FrameworkUtr[] = [];
  public organizations: Organization[] = [];

  public groupTypes = [
    this.customType,
    this.initiativeType,
    this.organizationType,
  ];

  constructor(
    private FrameworkUtrApi: FrameworkUtrApi,
    private notificationService: NotificationService,
  ) {
  }

  ngOnInit() {
    this.FrameworkUtrApi.list().toPromise()
      .then((resp) => {
        this.isLoaded = true;
        this.frameworkUtr = resp.data;
      })
      .catch(this.notificationService.errorHandler);
  }

  filterInitiatives(val) {
    if (!val) {
      return this.initiatives;
    }

    return this.initiatives.filter(initiative => {
      const regex = new RegExp(`${val}`, 'gi');
      return regex.test(initiative.name) || regex.test(initiative.code);
    });
  }

  filterOrganizations(val) {
    if (!val) {
      return this.organizations;
    }

    return this.organizations.filter(initiative => {
      const regex = new RegExp(`${val}`, 'gi');
      return regex.test(initiative.name) || regex.test(initiative.code);
    });
  }

  filterUtr(val, currentUtrs: GroupMd) {
    if (!val) {
      return this.frameworkUtr.filter(u => !currentUtrs.utrs.includes(u._id));;
    }

    const frameworkUtrs = this.frameworkUtr.filter((utr: FrameworkUtr) => {
      const regex = new RegExp(`${val}`, 'gi');
      return regex.test(utr.name) || regex.test(utr.code);
    });
    return frameworkUtrs.filter(u => !currentUtrs.utrs.includes(u._id));
  }

  public addUtr(group: GroupMd, utr: FrameworkUtr) {
    if (group.utrs.includes(utr._id)) {
      return;
    }
    group.utrs.push(utr._id);
    group.utrControl.setValue('');
  }

  public addGroup(newGroup?: any) {

    const group = newGroup || {
      objectType: this.customType,
      object: { name: '', logo: '', },
      utrs: [],
    }

    const name = this.getName(group)
    const formControl = new FormControl()
    const orgControl = new FormControl()
    const utrControl = new FormControl()
    const items = {
      ...group,
      control: formControl,
      name: name,
      id: this.createId(),
      filteredInitiatives: formControl.valueChanges.pipe(
        startWith(null),
        debounceTime(200),
        distinctUntilChanged(),
        map(val => this.filterInitiatives(val)),),
      orgControl: orgControl,
      filteredOrganizations: orgControl.valueChanges.pipe(
        startWith(null),
        debounceTime(200),
        distinctUntilChanged(),
        map(val => this.filterOrganizations(val)),),
      utrControl: utrControl,
      filteredUtrs: utrControl.valueChanges.pipe(
        startWith(null),
        debounceTime(200),
        distinctUntilChanged(),
        map(val => this.filterUtr(val, items)),),
    };
    this.groups.push(items);
  }


  private createId() {
    return '_' + Math.random().toString(36).substr(2, 9)
  }

  private getName(group: Group) {
    switch (group.objectType) {
      case this.organizationType:
        const org = this.organizations.find(i => i._id === group.object.objectId)
        return org ? org.name : ''
      case this.initiativeType:
        const initiative = this.initiatives.find(i => i._id === group.object.objectId)
        return initiative ? initiative.name : ''
      default:
        return '';
    }
  }

  public displayGroups(groups: Group[]) {
    if (Array.isArray(groups)) {
      groups.forEach((g) => this.addGroup(g))
    }
  }

  public getGroups() {
    return this.groups.map(g => {
      const { objectType, object, utrs } = g
      return { objectType, object, utrs }
    })
  }

  public changeGroupType(type: string, group: GroupMd) {
    group.object.objectId = undefined;
    group.name = '';
  }

  public setOrganizations(org: Organization[]) {
    this.organizations = org;
  }

  public setInitiatives(initiatives: any[]) {
    this.initiatives = initiatives;
  }

  public removePhase(phaseIndex) {
    this.groups.splice(phaseIndex, 1);
  }

  public removeItem(item: any, list: any[]): void {
    list.splice(list.indexOf(item), 1);
  }

  displayUtr(utrId: string) {
    const utr = this.frameworkUtr.find(utr => utr._id === utrId);
    if (!utr) {
      return 'Look up failed';
    }

    return utr.name ? `${utr.name} (${utr._id})` : utr._id;
  }

  renderItem(item: any) {
    if (item.name) {
      return item.name;
    }

    if (item.object) {
      return item.object.name || item.object.objectId || ' - ';
    }

    return ' - ';
  }
}
